import clsx from 'clsx';
import PropTypes from 'prop-types';
import { memo, useCallback, forwardRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { HyperlinkShape } from '../../api/types';
import { useApiContext } from '../../context/api-provider';
import { useTrackEvent } from '../../hooks/use-track-event';
import { RouteLink } from '../route-link/route-link';

const HyperlinkUI = forwardRef(({ className, labelId, url, isActive, children, trackingData, ...props }, ref) => {
  const {
    helpers: { resolvePlatformUrl },
  } = useApiContext();
  const content = labelId ? <FormattedMessage id={labelId} /> : children;
  const calculatedClassName = clsx(className, isActive && 'isActive');
  const { trackEvent } = useTrackEvent();

  const defaultOnClick = useCallback(
    event => {
      if (trackingData) {
        trackEvent(trackingData);
      }
      props.onClick?.(event);
    },
    [props.onClick, trackEvent, trackingData]
  );

  if (!url) {
    return (
      <span ref={ref} className={calculatedClassName} {...props}>
        {content}
      </span>
    );
  }

  if (url.startsWith('/')) {
    return (
      <RouteLink
        ref={ref}
        activeClassName="isActive"
        className={calculatedClassName}
        to={url}
        trackingData={trackingData}
        {...props}>
        {content}
      </RouteLink>
    );
  }

  const resolvedUrl = resolvePlatformUrl(url);

  return (
    <a
      ref={ref}
      className={calculatedClassName}
      href={resolvedUrl}
      role="button"
      tabIndex="-1"
      {...props}
      onClick={defaultOnClick}
      onKeyPress={defaultOnClick}>
      {content}
    </a>
  );
});

HyperlinkUI.propTypes = {
  className: PropTypes.string,
  ...HyperlinkShape,
  children: PropTypes.node,
};

HyperlinkUI.defaultProps = {
  className: undefined,
  children: undefined,
};

export const Hyperlink = memo(HyperlinkUI);
